import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} zoomedGallery - The zoomed gallery scroll container.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'zoomedGallery'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {number} */
  #selectedIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.zoomedGallery.addEventListener('scroll', this.handleScroll);
    this.refs.zoomedGallery.addEventListener('wheel', this.handleWheel, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.zoomedGallery.removeEventListener('scroll', this.handleScroll);
    this.refs.zoomedGallery.removeEventListener('wheel', this.handleWheel);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails, zoomedGallery } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];
    this.#selectedIndex = index;

    const open = () => {
      dialog.showModal();

      targetImage?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });

      if (targetThumbnail instanceof HTMLElement) {
        scrollIntoView(targetThumbnail, {
          ancestor: thumbnails,
          behavior: 'instant',
          block: 'center',
          inline: 'center',
        });
      }

      zoomedGallery.scrollTo({
        left: targetImage?.offsetLeft ?? 0,
        behavior: 'instant',
      });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (targetImage && this.#canUseGsap()) {
      open();
      await this.#animateOpen(targetImage);
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails, zoomedGallery } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media, zoomedGallery);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#selectedIndex = activeIndex;
    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Converts vertical mouse-wheel movement into horizontal gallery movement on desktop.
   * @param {WheelEvent} event - The wheel event.
   */
  handleWheel = (event) => {
    const { zoomedGallery } = this.refs;
    if (zoomedGallery.scrollWidth <= zoomedGallery.clientWidth) return;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

    event.preventDefault();
    zoomedGallery.scrollBy({
      left: event.deltaY,
      behavior: 'auto',
    });
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media, zoomedGallery } = this.refs;

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media, zoomedGallery);

    if (this.#canUseGsap()) {
      await this.#animateClose(mostVisibleElement);
      this.closeDialog();
      return;
    }

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  #canUseGsap() {
    return !prefersReducedMotion() && Boolean(window.gsap && typeof window.gsap.timeline === 'function');
  }

  /**
   * Animates the zoom dialog when it opens.
   * @param {HTMLElement} targetImage - The active media container.
   * @returns {Promise<void>}
   */
  #animateOpen(targetImage) {
    const gsap = window.gsap;
    if (!gsap) return Promise.resolve();

    const { dialog, thumbnails } = this.refs;
    const media = targetImage.querySelector('.product-media');
    const mediaTargets = [media].filter(Boolean);
    const controls = [
      thumbnails,
      dialog.querySelector('.dialog-zoomed-gallery__close-button'),
      ...dialog.querySelectorAll('.dialog-zoomed-gallery__arrow'),
    ].filter(Boolean);

    return new Promise((resolve) => {
      gsap.killTweensOf([dialog, ...mediaTargets, ...controls]);

      gsap
        .timeline({
          defaults: { overwrite: 'auto' },
          onComplete: () => {
            gsap.set([dialog, ...mediaTargets, ...controls], {
              clearProps: 'opacity,visibility,transform,filter',
            });
            resolve();
          },
        })
        .fromTo(dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.22, ease: 'power2.out' }, 0)
        .fromTo(
          mediaTargets,
          { autoAlpha: 0, y: 18, scale: 0.975, filter: 'blur(5px)' },
          { autoAlpha: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.58, ease: 'power3.out' },
          0.03
        )
        .fromTo(controls, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.32, stagger: 0.035 }, 0.18);
    });
  }

  /**
   * Animates the zoom dialog before it closes.
   * @param {HTMLElement} targetImage - The active media container.
   * @returns {Promise<void>}
   */
  #animateClose(targetImage) {
    const gsap = window.gsap;
    if (!gsap) return Promise.resolve();

    const { dialog, thumbnails } = this.refs;
    const media = targetImage.querySelector('.product-media');
    const mediaTargets = [media].filter(Boolean);
    const controls = [
      thumbnails,
      dialog.querySelector('.dialog-zoomed-gallery__close-button'),
      ...dialog.querySelectorAll('.dialog-zoomed-gallery__arrow'),
    ].filter(Boolean);

    return new Promise((resolve) => {
      gsap.killTweensOf([dialog, ...mediaTargets, ...controls]);

      gsap
        .timeline({
          defaults: { overwrite: 'auto' },
          onComplete: resolve,
        })
        .to(controls, { autoAlpha: 0, y: 8, duration: 0.16, ease: 'power2.in' }, 0)
        .to(mediaTargets, { autoAlpha: 0, y: 10, scale: 0.985, filter: 'blur(4px)', duration: 0.24, ease: 'power2.in' }, 0)
        .to(dialog, { autoAlpha: 0, duration: 0.24, ease: 'power2.inOut' }, 0.04);
    });
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPrevious(event);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNext(event);
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Selects the previous media item.
   * @param {Event} event - The click or keyboard event.
   */
  showPrevious(event) {
    event.preventDefault();
    event.stopPropagation();

    const mediaCount = this.refs.media.length;
    if (mediaCount <= 1) return;

    const index = (this.#selectedIndex - 1 + mediaCount) % mediaCount;
    this.selectThumbnail(index);
  }

  /**
   * Selects the next media item.
   * @param {Event} event - The click or keyboard event.
   */
  showNext(event) {
    event.preventDefault();
    event.stopPropagation();

    const mediaCount = this.refs.media.length;
    if (mediaCount <= 1) return;

    const index = (this.#selectedIndex + 1) % mediaCount;
    this.selectThumbnail(index);
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails, zoomedGallery } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    this.#selectedIndex = index;
    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
        block: 'nearest',
        inline: 'start',
      });

      zoomedGallery.scrollTo({
        left: targetImage.offsetLeft,
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @param {Element | null} [root] - The scroll container used as the visibility root.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements, root = null) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        root,
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
